import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Button,
  CardMedia,
  Checkbox,
  FormControlLabel,
  Input,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  CircularProgress,
  FormHelperText
} from "@mui/material";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import { ChangeEvent, useState, useEffect } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from "@mui/icons-material/Close";
import {
  useGetCategoriesQuery,
  useGetBrandsQuery,
  useGetModelsQuery,
  useGetVehicleTypesQuery,
  useGetFuelTypesQuery,
  useGetEnginePowerQuery,
  useGetTransmissionQuery,
  useGetDriveTypesQuery,
  useGetEmissionClassQuery,
  useGetEnvironmentalBadgeQuery,
  // useGetConditionsQuery,
  useGetColorsQuery,
  useGetFeatureGroupsQuery,
  useGetDamageTypeQuery,
  useGetAxlesQuery,
  useAddVehicleMutation,
  useUpdateVehicleMutation,
  useUploadMediaMutation,
  useGetVariantsQuery,
  useGetCategoryDetailsQuery,
  useGetVehicleDetailQuery,
} from "../../service/features/api";
import { Group_Type, VEHICLE_TYPE, Field_Type, PAYMENT_MODE, VEHICLE_CONDITION, RENTAL_DURATION } from "../../constants/enum";
import { getVehicleTypeLabel } from "../../utils/vehicle";
import { toast } from "react-toastify";
import { useUploadBlogImagesMutation } from "../../service/blogs/api";
import { useGetShowroomsQuery } from "../../service/showroom/api";

type FeatureSubGroup = {
  name: string;
  features: string[];
};

type FeatureGroup = {
  name: string;
  type: "checkbox" | "select" | "mixed" | "group";
  features?: string[];
  options?: string[];
  selectOptions?: string[];
  subGroups?: FeatureSubGroup[];
};

const AddEditVehicle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isAddUser = !id;
  const { data: vehicleResponse } = useGetVehicleDetailQuery(id, { skip: !id });
  const [vehicleType, setVehicleType] = useState(location.state?.type || 1);

  // Mutations
  const [addVehicle, { isLoading: isAdding }] = useAddVehicleMutation();
  const [updateVehicle, { isLoading: isUpdating }] = useUpdateVehicleMutation();
  const [uploadBlogImages, { isLoading: isUploading }] = useUploadBlogImagesMutation();

  const [isEdit, setIsEdit] = useState(false);
  const [vehicleId, setVehicleId] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showroomImages, setShowroomImages] = useState<string[]>([]);
  // Additional State for Form Fields
  const [adTitle, setAdTitle] = useState("");
  const [price, setPrice] = useState("");
  const [locationName, setLocationName] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [fullDesc, setFullDesc] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [availability, setAvailability] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);
  const [isLuxury, setIsLuxury] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  // const [isBlackWeek, setIsBlackWeek] = useState(false);

  // Technical State
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [maxWeight, setMaxWeight] = useState("");
  const [miles, setMiles] = useState("no"); // Radio state for Miles (Yes/No)

  // (Removed local getVehicleTypeLabel as it's now imported)

  const typeLabel = getVehicleTypeLabel(vehicleType);
  const [drivenKms, setDrivenKms] = useState(""); // For "Driven Kms" input
  const [milage, setMilage] = useState(""); // New field
  const [fuelType, setFuelType] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedTransmission, setSelectedTransmission] = useState("");
  const [selectedDriveType, setSelectedDriveType] = useState("");
  const [selectedEmission, setSelectedEmission] = useState("");
  const [selectedBadge, setSelectedBadge] = useState("");
  const [selectedEnginePower, setSelectedEnginePower] = useState("");
  const [selectedVehicleType, setSelectedVehicleType] = useState("");
  const [owners, setOwners] = useState("");
  const [seats, setSeats] = useState("");
  const [gcc, setGcc] = useState("default");

  const [selectedCondition, setSelectedCondition] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [constructionYear, setConstructionYear] = useState<any>(null);
  const [isElectric, setIsElectric] = useState("");
  const [huValidDate, setHuValidDate] = useState<any>(null);
  const [rentalDuration, setRentalDuration] = useState("");
  const [operationHours, setOperationHours] = useState("");
  const [batteryCapacity, setBatteryCapacity] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedDealer, setSelectedDealer] = useState("");

  // Dynamic Features State
  const [featureValues, setFeatureValues] = useState<Record<string, any>>({});
  const handleFeatureChange = (id: string, value: any) => {
    setFeatureValues(prev => ({ ...prev, [id]: value }));
  };

  // States for Dependent Dropdowns
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedBrand, setSelectedBrand] = useState<string>("");
  const [selectedVariant, setSelectedVariant] = useState<string>("");

  // Queries
  const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: categoryDetails, isLoading: isSubCatLoading } = useGetCategoryDetailsQuery(selectedCategory, { skip: !selectedCategory });
  const { data: brandsData } = useGetBrandsQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: modelsData } = useGetModelsQuery({ page: 1, limit: 100, brandId: selectedBrand, type: vehicleType });
  const { data: variantsData } = useGetVariantsQuery({ page: 1, limit: 100, modelId: selectedModel, type: vehicleType }, { skip: !selectedModel });
  // const { data: conditionsData } = useGetConditionsQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: vehicleTypesData } = useGetVehicleTypesQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: fuelTypesData } = useGetFuelTypesQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: enginePowerData } = useGetEnginePowerQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: transmissionData } = useGetTransmissionQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: driveTypesData } = useGetDriveTypesQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: emissionClassData } = useGetEmissionClassQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: environmentalBadgeData } = useGetEnvironmentalBadgeQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: interiorColorsData } = useGetColorsQuery({ page: 1, limit: 100, groupType: Group_Type.INTERIOR, type: vehicleType }); // Assuming 1 is Interior
  const { data: exteriorColorsData } = useGetColorsQuery({ page: 1, limit: 100, groupType: Group_Type.EXTERIOR, type: vehicleType }); // Assuming 2 is Exterior
  const { data: featureGroupsData } = useGetFeatureGroupsQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: damageTypeData } = useGetDamageTypeQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: axlesData } = useGetAxlesQuery({ page: 1, limit: 100, type: vehicleType });
  const { data: showroomsData } = useGetShowroomsQuery({ page: 1, limit: 100 });
  // Transforming data for UI
  const showrooms = showroomsData?.data?.data || [];

  // Transforming data for UI
  const categories = categoriesData?.data?.data || [];
  const brands = brandsData?.data?.data || [];
  const models = modelsData?.data?.data || [];
  const variants = variantsData?.data?.data || [];
  // const conditions = conditionsData?.data?.data || [];
  const vehicleTypes = vehicleTypesData?.data?.data || [];
  const fuelTypes = fuelTypesData?.data?.data || [];
  const enginePowers = enginePowerData?.data?.data || [];
  const transmissions = transmissionData?.data?.data || [];
  const driveTypes = driveTypesData?.data?.data || [];
  const emissionClasses = emissionClassData?.data?.data || [];
  const environmentalBadges = environmentalBadgeData?.data?.data || [];
  const interiorColors = interiorColorsData?.data?.data || [];
  const exteriorColors = exteriorColorsData?.data?.data || [];
  const featureGroups = featureGroupsData?.data?.data || [];
  const damageTypes = damageTypeData?.data?.data || [];
  const axlesList = axlesData?.data?.data || [];
  const subCategories = categoryDetails?.data?.subCategories || [];

  const [selectedInteriorColor, setSelectedInteriorColor] = useState("");
  const [selectedExteriorColor, setSelectedExteriorColor] = useState("");
  const [selectedAxle, setSelectedAxle] = useState("");
  const [selectedDamageType, setSelectedDamageType] = useState("");

  const [year, setYear] = useState<any>(null);
  const [month, setMonth] = useState<any>(null);

  useEffect(() => {
    const data = vehicleResponse?.data || location.state?.vehicle;
    if (data) {
      const v = data;
      const getID = (item: any) => {
        if (!item) return "";
        if (typeof item === 'string') return item;
        return item._id || item.id || "";
      };

      setIsEdit(true);
      setVehicleId(v._id);
      setAdTitle(v.name || "");
      setPrice(v.price?.toString() || "");
      setLocationName(v.address?.city || "");
      setShortDesc(v.sortDescription || "");
      setFullDesc(v.description || "");
      setVehicleNumber(v.registrationNo || "");
      setIsFeatured(v.isFeatured || false);
      setIsLuxury(v.isLuxury || false);
      setLength(v.length?.toString() || "");
      setWidth(v.width?.toString() || "");
      setHeight(v.height?.toString() || "");
      setMaxWeight(v.weight?.toString() || "");
      setGcc(v.gcc === true || v.gcc === "yes" ? "yes" : "no");
      setMiles(v.miles === true ? "yes" : "no");
      setSelectedCategory(getID(v.categoryId));
      setSelectedBrand(getID(v.brandId));
      setSelectedModel(getID(v.modelId));
      setSelectedVariant(getID(v.variantId));
      setSelectedCondition(v.condition || "");
      setFuelType(v.fuelTypeId || "");
      setZipCode(v.address?.zip || "");
      setPaymentMode(v.paymentMethod || "");
      setSelectedTransmission(getID(v.transmissionId));
      setSelectedEnginePower(getID(v.engineDetailId));
      setSelectedVehicleType(getID(v.vehicleTypeId));
      setIsElectric(v.isElectric ? "yes" : "no");
      setRentalDuration(v.rentalDuration?.toString() || "");
      setOperationHours(v.operationHours?.toString() || "");
      setBatteryCapacity(v.batteryCapacity?.toString() || "");
      setDrivenKms(v.drivenKms?.toString() || "");
      setMilage(v.milage?.toString() || "");
      setSeats(v.seats?.toString() || "");
      setAvailability(v.isAvailable ? "yes" : "no");
      setHuValidDate(v.huValid ? dayjs(v.huValid) : null);
      setVideoUrl(v.videoUrl || "");
      setShowroomImages(v.images || []);
      setSelectedInteriorColor(getID(v.interiorColorId));
      setSelectedExteriorColor(getID(v.exteriorColorId));
      setSelectedSubCategory(getID(v.subCategoryId));
      setYear(v.registrationYear ? dayjs().year(v.registrationYear) : null);
      setConstructionYear(v.manufactureYear ? dayjs().year(v.manufactureYear) : null);
      setSelectedAxle(getID(v.axlesId));
      setSelectedDamageType(getID(v.damageTypeId));
      setSelectedDriveType(getID(v.driveTypeId));
      setSelectedBadge(getID(v.environmentalBadgeId));
      setSelectedEmission(getID(v.emissionClassId));
      setSelectedDealer(getID(v.dealerId));
      if (v.type) setVehicleType(v.type);
    }
  }, [vehicleResponse?.data, location.state?.vehicle]);

  useEffect(() => {
    const data = vehicleResponse?.data || location.state?.vehicle;
    if (data?.features && featureGroups.length > 0) {
      const vFeatures = data.features;
      const newFeatureValues: Record<string, any> = {};

      // Helper to get ID regardless of object structure
      const getId = (item: any) => {
        if (!item) return "";
        if (typeof item === 'string') return item;
        return item._id || item.id || "";
      };

      featureGroups.forEach((group: any) => {
        const groupId = getId(group) || (typeof group.name === 'object' ? group.name?.en || group.name?.name : group.name);
        const listItems = group.features?.length ? group.features : (group.options?.length ? group.options : []);

        if (group.fieldType === Field_Type.CHECKBOX) {
          const selectedInGroup = listItems
            .filter((f: any) => vFeatures.some((vf: any) => getId(vf) === getId(f)))
            .map((f: any) => getId(f));

          if (selectedInGroup.length > 0) {
            newFeatureValues[groupId] = selectedInGroup;
          }
        } else {
          const selected = listItems.find((f: any) => vFeatures.some((vf: any) => getId(vf) === getId(f)));
          if (selected) {
            newFeatureValues[groupId] = getId(selected);
          }
        }
      });
      setFeatureValues(newFeatureValues);
    }
  }, [vehicleResponse?.data, location.state?.vehicle, featureGroups]);

  const allowOnlyNumbers = (value: string) => {
    return value.replace(/[^0-9]/g, "");
  };


  const handleSubmit = async () => {
    // console.log("handleSubmit");
    const newErrors: Record<string, string> = {};
    if (!selectedBrand) newErrors.selectedBrand = "Brand is required";
    if (!selectedModel) newErrors.selectedModel = "Model is required";
    if (!adTitle?.trim()) newErrors.adTitle = "Ad Title is required";
    if (!year) newErrors.year = "Year is required";
    if (!price) newErrors.price = "Price is required";
    if (!milage) newErrors.milage = "Milage is required";

    if ([VEHICLE_TYPE.BIKE, VEHICLE_TYPE.BICYCLE].includes(vehicleType)) {
      if (!selectedExteriorColor) {
        toast.error("Color is required");
        newErrors.color = "Color is required";
      }
    } else {
      if (!selectedInteriorColor || !selectedExteriorColor) {
        toast.error("Interior and Exterior colors are required");
        newErrors.color = "Colors are required";
      }
    }


    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill all required fields");
      return;
    }
    setErrors({});
    // console.log("Submitting with:", { selectedCategory, selectedBrand });
    // console.log("payload",);

    const payload: any = {
      type: vehicleType,
      sellerType: 1, // Personal
      isFeatured: isFeatured,
      isLuxury: isLuxury,
      brandId: selectedBrand,
      modelId: selectedModel,
      price: Number(price),
      milage: Number(milage),
    };

    if (selectedCategory) payload.categoryId = selectedCategory;
    if (selectedDealer) payload.dealerId = selectedDealer;
    if (adTitle) payload.name = adTitle;
    if (showroomImages.length > 0) payload.images = showroomImages.filter((img) => img);
    
    if (gcc !== "default") payload.gcc = gcc === "yes";
    if (miles) payload.miles = miles === "yes";
    
    if (selectedVariant) payload.variantId = selectedVariant;
    if (shortDesc) {
      payload.sortDescription = shortDesc;
      payload.description = shortDesc;
    }
    
    const selectedFeatures = Object.entries(featureValues).reduce((acc: string[], [id, value]) => {
      if (!value) return acc;
      const group = featureGroups.find((g: any) => (g._id || g.id) === id);
      if (group?.fieldType === Field_Type.BOOLEAN) {
        if (value === "yes") acc.push(id);
      } else if (group?.fieldType === Field_Type.CHECKBOX) {
        if (Array.isArray(value)) acc.push(...value);
      } else if (group?.fieldType === Field_Type.SELECT || group?.fieldType === Field_Type.RADIO) {
        if (typeof value === 'string' && value !== "yes" && value !== "no") acc.push(value);
      }
      return acc;
    }, []);
    
    if (selectedFeatures.length > 0) payload.features = selectedFeatures;
    if (videoUrl) payload.videoUrl = videoUrl;
    if (paymentMode) payload.paymentMethod = Number(paymentMode);
    if (isElectric) payload.isElectric = isElectric === "yes";
    
    payload.location = { type: "Point", coordinates: [0, 0] };
    payload.address = {
      line1: locationName || "Default Address",
      city: locationName || "Default City",
      state: "Default State",
      country: "Default Country",
      zip: zipCode || undefined
    };
    
    if (availability) payload.isAvailable = availability === "yes";
    
    payload.contactInfo = {
      phone: "0000000000",
      email: "default@example.com"
    };

    if (constructionYear) payload.manufactureYear = constructionYear.year();
    if (year) payload.registrationYear = year.year();
    if (huValidDate) payload.huValid = huValidDate.toDate();

    if (vehicleNumber) payload.registrationNo = vehicleNumber;
    if (Number(maxWeight) > 0) payload.weight = Number(maxWeight);

    if (selectedInteriorColor) payload.interiorColorId = selectedInteriorColor;
    if (selectedExteriorColor) payload.exteriorColorId = selectedExteriorColor;
    
    if (selectedAxle) payload.axlesId = selectedAxle;
    if (selectedDamageType) payload.damageTypeId = selectedDamageType;
    if (selectedDriveType) payload.driveTypeId = selectedDriveType;
    if (selectedEmission && isElectric !== "yes") payload.emissionClassId = selectedEmission;
    if (selectedEnginePower) payload.engineDetailId = selectedEnginePower;
    if (selectedTransmission) payload.transmissionId = selectedTransmission;
    if (fuelType && isElectric !== "yes") payload.fuelTypeId = fuelType;
    if (selectedVehicleType) payload.vehicleTypeId = selectedVehicleType;
    if (selectedSubCategory) payload.subCategoryId = selectedSubCategory;
    if (selectedCondition) payload.condition = Number(selectedCondition);
    if (Number(drivenKms) > 0) payload.drivenKms = Number(drivenKms);
    if (isElectric === "yes" && Number(batteryCapacity) > 0) payload.batteryCapacity = Number(batteryCapacity);
    if (Number(seats) > 0) payload.seats = Number(seats);



    try {
      if (isEdit) {
        await updateVehicle({ id: vehicleId, ...payload }).unwrap();
        toast.success("Vehicle updated successfully");
      } else {
        await addVehicle(payload).unwrap();
        toast.success("Vehicle added successfully");
      }
      navigate("/manage-vehicles");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to save vehicle");
    }
  };



  return (
    <>
      <CommonBreadcrumb
        title={isAddUser ? "إضافة / Add " + (typeLabel || "Vehicle") : "تعديل / Edit " + (typeLabel || "Vehicle")}
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          { label: "المركبات المدرجة / Listed Vehicles", onClick: () => navigate("/manage-vehicles") },
          { label: isAddUser ? "إضافة / Add " + (typeLabel || "Vehicle") : "تعديل / Edit " + (typeLabel || "Vehicle") },
        ]}
      />
      <div className="form">

        <div className="cards">
          <div className="table_header mb_20">
            <h2>تحميل صور وفيديو المركبة / Upload Vehicle Images & Video</h2>
          </div>

          <div className="gap_p">
            {/* Image Upload */}
            <div className="control_group w_100">
              <label>تحميل الصور / Upload Images <small>- 0 of 15 Images (JPG, max. 10 MB per Image)</small></label>
              <div className="gap_p">
                {/* Upload Button — always first/leftmost */}
                {showroomImages.length < 20 && (
                  <div className="w_25">
                    <label
                      className="upload_image no_radius v2"
                      style={{ cursor: isUploading ? "not-allowed" : "pointer" }}
                      htmlFor="vehicle-image-upload"
                    >
                      <Input
                        sx={{ display: "none" }}
                        id="vehicle-image-upload"
                        type="file"
                        inputProps={{ accept: "image/*", multiple: true }}
                        disabled={isUploading}
                        onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                          const files = event.target.files;
                          if (files && files.length > 0) {
                            if (showroomImages.length + files.length > 20) {
                              toast.error(`You can upload a maximum of 20 images. You can add ${20 - showroomImages.length} more.`);
                              event.target.value = "";
                              return;
                            }
                            const formData = new FormData();
                            Array.from(files).forEach((file) => {
                              formData.append("files", file);
                            });

                            try {
                              const res = await uploadBlogImages(formData).unwrap();
                              const imageUrls = res?.data || res;
                              if (Array.isArray(imageUrls)) {
                                setShowroomImages([...showroomImages, ...imageUrls]);
                                toast.success("Images uploaded successfully");

                              }
                            } catch (err: any) {
                              toast.error(err?.data?.message || "Upload failed");
                            }
                            event.target.value = "";
                          }
                        }}
                      />
                      <span className="upload_image_holder">
                        {isUploading ? (
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                            <CircularProgress size={24} />
                          </div>
                        ) : (
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt=""
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt=""
                              />
                            </span>
                          </figure>
                        )}
                      </span>
                    </label>
                  </div>
                )}

                {/* Image Previews — rendered after the upload button */}
                {showroomImages.map((img, i) => (
                  <div className="w_25" key={i}>
                    <div className="upload_image no_radius v2">
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia component="img" image={img} />
                        </figure>
                        <CloseIcon
                          className="remove_img"
                          onClick={() => {
                            const newImages = showroomImages.filter((_, index) => index !== i);
                            setShowroomImages(newImages);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {errors.showroomImages && <FormHelperText error>{errors.showroomImages}</FormHelperText>}
            </div>


            {/* Video Upload */}
            <div className="control_group w_100">
              <label>فيديو يوتيوب / YouTube video</label>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="https://www.youtube.com/watch?v=zyx"
                value={videoUrl}
                onChange={(e) => {
                  setVideoUrl(e.target.value);
                  if (errors.videoUrl) setErrors(prev => ({ ...prev, videoUrl: "" }));
                }}
                error={!!errors.videoUrl}
                helperText={errors.videoUrl}
              />
            </div>
          </div>
        </div>

        <div className="cards">
          <div className="table_header mb_20">
            <h2>المعلومات الأساسية / Basic Information</h2>
          </div>

          <div className="gap_p">

            <div className="control_group w_33">
              <label>العلامة التجارية / Brand <span className="c_danger">*</span></label>
              <Select
                fullWidth
                hiddenLabel
                value={selectedBrand}
                onChange={(e) => {
                  setSelectedBrand(e.target.value);
                  if (e.target.value) setErrors(prev => ({ ...prev, selectedBrand: "" }));
                }}
                displayEmpty
                error={!!errors.selectedBrand}
              >
                <MenuItem value="" disabled>حدد العلامة التجارية / Select Brand</MenuItem>
                {brands.map((brand: any) => (
                  <MenuItem key={brand._id} value={brand._id}>
                    {typeof brand.name === 'object' ? brand.name?.en || brand.name?.name || "" : brand.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.selectedBrand && <FormHelperText error>{errors.selectedBrand}</FormHelperText>}
            </div>

            <div className="control_group w_33">
              <label>الموديل / Model <span className="c_danger">*</span></label>
              <Select
                fullWidth
                hiddenLabel
                value={selectedModel}
                onChange={(e) => {
                  setSelectedModel(e.target.value);
                  setSelectedVariant("");
                  if (e.target.value) setErrors(prev => ({ ...prev, selectedModel: "" }));
                }}
                displayEmpty
                error={!!errors.selectedModel}
              >
                <MenuItem value="" disabled>حدد الموديل / Select Model</MenuItem>
                {models.map((model: any) => (
                  <MenuItem key={model._id} value={model._id}>
                    {typeof model.name === 'object' ? model.name?.en || model.name?.name || "" : model.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.selectedModel && <FormHelperText error>{errors.selectedModel}</FormHelperText>}
            </div>

            <div className="control_group w_33">
              <label>الفئة / Variant</label>
              <Select
                fullWidth
                hiddenLabel
                value={selectedVariant}
                onChange={(e) => setSelectedVariant(e.target.value)}
                displayEmpty
                disabled={!selectedModel}
              >
                <MenuItem value="" disabled>حدد الفئة / Select Variant</MenuItem>
                {variants.map((variant: any) => (
                  <MenuItem key={variant._id} value={variant._id}>
                    {typeof variant.name === 'object' ? variant.name?.en || variant.name?.name || "" : variant.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>أول تسجيل / First registration <span className="c_danger">*</span></label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={["year"]}
                  value={year}
                  onChange={(newValue) => setYear(newValue)}
                  className="form_control dateTime_picker"
                  disableFuture
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      id: "registration-year",
                      placeholder: "حدد السنة / Select Year",
                      hiddenLabel: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="control_group w_33">
              <label>سنة الصنع / Year of manufacturing</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={["year"]}
                  value={constructionYear}
                  onChange={(newValue) => setConstructionYear(newValue)}
                  className="form_control dateTime_picker"
                  disableFuture
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      id: "construction-year",
                      placeholder: "حدد السنة / Select Year",
                      hiddenLabel: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </div>



            <div className="control_group w_33">
              <label>رقم المركبة / Vehicle Number</label>
              <TextField
                placeholder="مثال / e.g. D9_647949"
                hiddenLabel
                fullWidth
                value={vehicleNumber}
                onChange={(e) => {
                  setVehicleNumber(e.target.value);
                  if (e.target.value.length >= 3) setErrors(prev => ({ ...prev, vehicleNumber: "" }));
                }}
                error={!!errors.vehicleNumber}
                helperText={errors.vehicleNumber}
              />
            </div>

            <div className="control_group w_33">
              <label>التوفر / Availability</label>
              <Select
                fullWidth
                hiddenLabel
                value={availability}
                onChange={(e) => {
                  setAvailability(e.target.value);
                  if (e.target.value) setErrors(prev => ({ ...prev, availability: "" }));
                }}
                displayEmpty
                error={!!errors.availability}
              >
                <MenuItem value="" disabled>حدد / Select</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
              {errors.availability && <FormHelperText error>{errors.availability}</FormHelperText>}
            </div>

            <div className="control_group w_33">
              <label>الكيلومترات المقطوعة / Driven KMs</label>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="أدخل الكيلومترات المقطوعة / Enter Driven KMs"
                value={drivenKms}
                onChange={(e) => {
                  setDrivenKms(allowOnlyNumbers(e.target.value));
                  if (e.target.value) setErrors(prev => ({ ...prev, drivenKms: "" }));
                }}
                error={!!errors.drivenKms}
                helperText={errors.drivenKms}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="size_sm">km</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>المسافة المقطوعة (ميلاج) / Milage <span className="c_danger">*</span></label>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="أدخل ميلاج / Enter Milage"
                value={milage}
                onChange={(e) => {
                  setMilage(allowOnlyNumbers(e.target.value));
                  if (e.target.value) setErrors(prev => ({ ...prev, milage: "" }));
                }}
                error={!!errors.milage}
                helperText={errors.milage}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="size_sm">km/l</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_100">
              <label>عنوان الإعلان / Ad Title <span className="c_danger">*</span></label>
              <TextField
                placeholder="أدخل عنوان الإعلان / Enter Ad Title"
                hiddenLabel
                fullWidth
                value={adTitle}
                onChange={(e) => {
                  setAdTitle(e.target.value);
                  if (e.target.value.trim()) setErrors(prev => ({ ...prev, adTitle: "" }));
                }}
                error={!!errors.adTitle}
                helperText={errors.adTitle}
              />
            </div>


            <div className="control_group w_33">
              <label>الفئة / Category</label>
              <Select
                fullWidth
                hiddenLabel
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubCategory(""); // Reset sub-category
                  if (e.target.value) setErrors(prev => ({ ...prev, selectedCategory: "" }));
                }}
                displayEmpty
                error={!!errors.selectedCategory}
              >
                <MenuItem value="" disabled>حدد الفئة / Select Category</MenuItem>
                {categories.map((cat: any) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {typeof cat.name === 'object' ? cat.name?.en || cat.name?.name || "" : cat.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.selectedCategory && <FormHelperText error>{errors.selectedCategory}</FormHelperText>}
            </div>

            <div className="control_group w_33">
              <label>الفئة الفرعية / Sub Category</label>
              <Select
                fullWidth
                hiddenLabel
                value={selectedSubCategory}
                onChange={(e) => setSelectedSubCategory(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>{selectedCategory ? "حدد الفئة الفرعية / Select Sub Category" : "حدد الفئة أولاً / Select Category First"}</MenuItem>
                {isSubCatLoading ? (
                  <MenuItem value="" disabled>Loading...</MenuItem>
                ) : subCategories.length > 0 ? (
                  subCategories.map((sub: any) => (
                    <MenuItem key={sub._id} value={sub._id}>
                      {typeof sub.name === 'object' ? sub.name?.en || sub.name?.name || "" : sub.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>لا توجد فئات فرعية / No Sub Categories Found</MenuItem>
                )}
              </Select>
            </div>

            {/* Sub Category - functionality unclear or missing API, commenting out or using placeholder */}
            {/* <div className="control_group w_33">
              <label>Sub Category</label>
              <Select fullWidth hiddenLabel defaultValue="default">
                <MenuItem value="default" disabled>Select Sub Category</MenuItem>
              </Select>
            </div> */}

            <div className="control_group w_33">
              <label>المعرض / Showroom</label>
              <Select
                fullWidth
                hiddenLabel
                value={selectedDealer}
                onChange={(e) => {
                  setSelectedDealer(e.target.value);
                  if (e.target.value) setErrors(prev => ({ ...prev, selectedDealer: "" }));
                }}
                displayEmpty
                error={!!errors.selectedDealer}
              >
                <MenuItem value="" disabled>حدد المعرض / Select Showroom</MenuItem>
                {showrooms.map((showroom: any) => (
                  <MenuItem key={showroom._id} value={showroom._id}>
                    {showroom.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.selectedDealer && <FormHelperText error>{errors.selectedDealer}</FormHelperText>}
            </div>

            <div className="control_group w_33">
              <label>السعر / Price <span className="c_danger">*</span></label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="أدخل السعر / Enter Price"
                value={price}
                error={!!errors.price}
                helperText={errors.price}
                onChange={(e) => {
                  setPrice(allowOnlyNumbers(e.target.value));
                  if (e.target.value) setErrors(prev => ({ ...prev, price: "" }));
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="adornment_select">
                      <Select fullWidth hiddenLabel defaultValue="default">
                        <MenuItem value="default">ثابت / Fixed</MenuItem>
                        <MenuItem value="2">قابل للتفاوض / Negotiable</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>الموقع / Location</label>
              <TextField
                placeholder="أدخل الموقع / Enter Location"
                hiddenLabel
                fullWidth
                value={locationName}
                onChange={(e) => {
                  setLocationName(e.target.value);
                  if (e.target.value) setErrors(prev => ({ ...prev, locationName: "" }));
                }}
                error={!!errors.locationName}
                helperText={errors.locationName}
              />
            </div>

            <div className="control_group w_33">
              <label>الرمز البريدي / Zip Code</label>
              <TextField
                placeholder="أدخل الرمز البريدي / Enter Zip Code"
                hiddenLabel
                fullWidth
                value={zipCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  if (val.length <= 8) setZipCode(val);
                  if (val) setErrors(prev => ({ ...prev, zipCode: "" }));
                }}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
              />
            </div>

            <div className="control_group w_33">
              <label>الحالة / Condition</label>
              <Select
                fullWidth
                hiddenLabel
                value={selectedCondition}
                onChange={(e) => {
                  setSelectedCondition(e.target.value);
                  if (e.target.value) setErrors(prev => ({ ...prev, selectedCondition: "" }));
                }}
                displayEmpty
                error={!!errors.selectedCondition}
              >
                <MenuItem value="" disabled>حدد الحالة / Select Condition</MenuItem>
                {Object.keys(VEHICLE_CONDITION)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_CONDITION as any)[key]}>
                      {key.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
              </Select>
              {errors.selectedCondition && <FormHelperText error>{errors.selectedCondition}</FormHelperText>}
            </div>

            <div className="control_group w_100">
              <label>الوصف / Description</label>
              <TextField
                multiline
                rows={4}
                placeholder="أدخل الوصف / Enter description"
                hiddenLabel
                fullWidth
                value={shortDesc}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.length === 1 && val === " ") val = "";
                  if (val.length <= 800) {
                    setShortDesc(val);
                  }
                }}
                inputProps={{ maxLength: 800 }}
              />
            </div>

            <div className="control_group flex w_100">
              <label>هل هذه مركبة مميزة؟ / Is this Featured Vehicle?</label>
              <SwitchToggle checked={isFeatured} onChange={() => setIsFeatured(!isFeatured)} />
            </div>

            {/* <div className="control_group flex w_100">
              <label>Leasing Black Week Deal?</label>
              <SwitchToggle checked={isBlackWeek} onChange={() => setIsBlackWeek(!isBlackWeek)} />
            </div> */}

            <div className="control_group flex w_100">
              <label>هل هذا منتج فاخر؟ / Is this a luxury product?</label>
              <SwitchToggle checked={isLuxury} onChange={() => setIsLuxury(!isLuxury)} />
            </div>

          </div>
        </div>

        <div className="cards">
          <div className="table_header mb_20">
            <h2>البيانات الفنية / Technical Data</h2>
          </div>

          <div className="gap_p">

            {vehicleType !== VEHICLE_TYPE.BIKE && (
              <div className="control_group w_33">
                <label>عدد المقاعد / Number of seats</label>
                <Select fullWidth hiddenLabel value={seats} onChange={(e) => setSeats(e.target.value)} displayEmpty>
                  <MenuItem value="" disabled>حدد / Select</MenuItem>
                  <MenuItem value="1">1</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                  <MenuItem value="3">3</MenuItem>
                  <MenuItem value="4">4</MenuItem>
                  <MenuItem value="5">5</MenuItem>
                  <MenuItem value="6">6</MenuItem>
                  <MenuItem value="7">7</MenuItem>
                  <MenuItem value="8">8</MenuItem>
                </Select>
              </div>
            )}

            {vehicleType !== VEHICLE_TYPE.BIKE && (
              <>
                <div className="control_group w_33">
                  <label>الطول / Length</label>
                  <TextField
                    id="price"
                    fullWidth
                    hiddenLabel
                    placeholder="أدخل الطول / Enter Length"
                    value={length}
                    onChange={(e) => setLength(allowOnlyNumbers(e.target.value))}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" className="size_sm">mm</InputAdornment>
                      ),
                    }}
                  />
                </div>

                <div className="control_group w_33">
                  <label>العرض / Width</label>
                  <TextField
                    id="price"
                    fullWidth
                    hiddenLabel
                    placeholder="أدخل العرض / Enter Width"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" className="size_sm">mm</InputAdornment>
                      ),
                    }}
                  />
                </div>
              </>
            )}

            <div className="control_group w_33">
              <label>{vehicleType === VEHICLE_TYPE.BIKE ? "ارتفاع المقعد / Seat Height" : "الارتفاع / Height"}</label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="أدخل الارتفاع / Enter Height"
                value={height}
                onChange={(e) => setHeight(allowOnlyNumbers(e.target.value))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="size_sm">mm</InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>الوزن الأقصى المسموح به / Maximum Weight Allowed</label>
              <TextField
                id="price"
                fullWidth
                hiddenLabel
                placeholder="أدخل الوزن الأقصى / Enter Maximum Weight Allowed"
                value={maxWeight}
                onChange={(e) => {
                  setMaxWeight(allowOnlyNumbers(e.target.value));
                  if (Number(e.target.value) > 0) setErrors(prev => ({ ...prev, maxWeight: "" }));
                }}
                error={!!errors.maxWeight}
                helperText={errors.maxWeight}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="size_sm">kg</InputAdornment>
                  ),
                }}
              />
            </div>

            {![VEHICLE_TYPE.BIKE, VEHICLE_TYPE.BICYCLE].includes(vehicleType) && (
              <div className="control_group w_33">
                <label>المحاور / Axles</label>
                <Select
                  fullWidth
                  hiddenLabel
                  value={selectedAxle}
                  onChange={(e) => setSelectedAxle(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>حدد المحاور / Select Axles</MenuItem>
                  {axlesList.map((item: any) => (
                    <MenuItem key={item._id} value={item._id}>
                      {typeof item.name === 'object' ? item.name?.en || item.name?.name || "" : item.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}

            <div className="control_group w_33">
              <label>كهربائي / Electric</label>
              <Select
                fullWidth
                hiddenLabel
                value={isElectric}
                onChange={(e) => {
                  setIsElectric(e.target.value);
                  if (e.target.value) setErrors(prev => ({ ...prev, isElectric: "" }));
                }}
                displayEmpty
                error={!!errors.isElectric}
              >
                <MenuItem value="" disabled>حدد خياراً / Select Option</MenuItem>
                <MenuItem value="yes">نعم / Yes</MenuItem>
                <MenuItem value="no">لا / No</MenuItem>
              </Select>
              {errors.isElectric && <FormHelperText error>{errors.isElectric}</FormHelperText>}
            </div>

            {isElectric !== "yes" && (
              <div className="control_group w_33">
                <label>نوع الوقود / Fuel Type</label>
                <Select fullWidth hiddenLabel value={fuelType} onChange={(e) => setFuelType(e.target.value)} displayEmpty>
                  <MenuItem value="" disabled>حدد الوقود / Select Fuel</MenuItem>
                  {fuelTypes.map((fuel: any) => (
                    <MenuItem key={fuel._id} value={fuel._id}>
                      {typeof fuel.name === 'object' ? fuel.name?.en || fuel.name?.name || "" : fuel.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}

            <div className="control_group w_33">
              <label>قوة المحرك / Engine Power</label>
              <Select fullWidth hiddenLabel value={selectedEnginePower} onChange={(e) => setSelectedEnginePower(e.target.value)} displayEmpty>
                <MenuItem value="" disabled>حدد / Select</MenuItem>
                {enginePowers.map((ep: any) => (
                  <MenuItem key={ep._id} value={ep._id}>
                    {typeof ep.name === 'object' ? ep.name?.en || ep.name?.name || "" : ep.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="control_group w_33">
              <label>ناقل الحركة / Transmission</label>
              <Select fullWidth hiddenLabel value={selectedTransmission} onChange={(e) => setSelectedTransmission(e.target.value)} displayEmpty>
                <MenuItem value="" disabled>حدد / Select</MenuItem>
                {transmissions.map((tr: any) => (
                  <MenuItem key={tr._id} value={tr._id}>
                    {typeof tr.name === 'object' ? tr.name?.en || tr.name?.name || "" : tr.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {![VEHICLE_TYPE.BIKE, VEHICLE_TYPE.BICYCLE].includes(vehicleType) && (
              <div className="control_group w_33">
                <label>نوع الدفع / Drive Type</label>
                <Select fullWidth hiddenLabel value={selectedDriveType} onChange={(e) => setSelectedDriveType(e.target.value)} displayEmpty>
                  <MenuItem value="" disabled>حدد / Select</MenuItem>
                  {driveTypes.map((dt: any) => (
                    <MenuItem key={dt._id} value={dt._id}>
                      {typeof dt.name === 'object' ? dt.name?.en || dt.name?.name || "" : dt.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}

            {(vehicleType !== VEHICLE_TYPE.BICYCLE && vehicleType !== VEHICLE_TYPE.BIKE && isElectric !== "yes") && (
              <div className="control_group w_33">
                <label>فئة الانبعاثات / Emission class</label>
                <Select fullWidth hiddenLabel value={selectedEmission} onChange={(e) => setSelectedEmission(e.target.value)} displayEmpty>
                  <MenuItem value="" disabled>حدد / Select</MenuItem>
                  {emissionClasses.map((ec: any) => (
                    <MenuItem key={ec._id} value={ec._id}>
                      {typeof ec.name === 'object' ? ec.name?.en || ec.name?.name || "" : ec.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}

            <div className="control_group w_33">
              <label>شارة البيئة / Environmental badge</label>
              <Select fullWidth hiddenLabel value={selectedBadge} onChange={(e) => setSelectedBadge(e.target.value)} displayEmpty>
                <MenuItem value="" disabled>حدد / Select</MenuItem>
                {environmentalBadges.map((eb: any) => (
                  <MenuItem key={eb._id} value={eb._id}>
                    {typeof eb.name === 'object' ? eb.name?.en || eb.name?.name || "" : eb.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* Vehicle Type selection was commented out or handled elsewhere */}





            {isElectric === "yes" && (
              <div className="control_group w_33">
                <label>سعة البطارية / Battery Capacity</label>
                <TextField
                  fullWidth
                  hiddenLabel
                  placeholder="أدخل سعة البطارية / Enter Battery Capacity"
                  value={batteryCapacity}
                  onChange={(e) => {
                    setBatteryCapacity(allowOnlyNumbers(e.target.value));
                    if (e.target.value) setErrors(prev => ({ ...prev, batteryCapacity: "" }));
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" className="size_sm">Wh</InputAdornment>
                    ),
                  }}
                  error={!!errors.batteryCapacity}
                  helperText={errors.batteryCapacity}
                />
              </div>
            )}

            {/* <div className="control_group w_33">
              <label>Number of owners of the vehicle</label>
              <Select fullWidth hiddenLabel value={owners} onChange={(e) => setOwners(e.target.value)} displayEmpty>
                <MenuItem value="" disabled>Select</MenuItem>
                <MenuItem value="1">1</MenuItem>
                <MenuItem value="2">2</MenuItem>
                <MenuItem value="3">3</MenuItem>
                <MenuItem value="4">4</MenuItem>
              </Select>
            </div> */}

            <div className="control_group w_33">
              <label>الأضرار / Damage</label>
              <Select
                fullWidth
                hiddenLabel
                value={selectedDamageType}
                onChange={(e) => setSelectedDamageType(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>حدد / Select</MenuItem>
                {damageTypes.map((dt: any) => (
                  <MenuItem key={dt._id} value={dt._id}>
                    {typeof dt.name === 'object' ? dt.name?.en || dt.name?.name || "" : dt.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {![VEHICLE_TYPE.BIKE, VEHICLE_TYPE.BICYCLE].includes(vehicleType) && (
              <div className="control_group w_33">
                <label>صالح حتى / HU valid until</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={huValidDate}
                    onChange={(newValue) => setHuValidDate(newValue)}
                    className="form_control dateTime_picker"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        id: "hu-valid",
                        placeholder: "حدد التاريخ / Select Date",
                        hiddenLabel: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
            )}

            {![VEHICLE_TYPE.BIKE, VEHICLE_TYPE.BICYCLE].includes(vehicleType) && (
              <>
                <div className="control_group w_33">
                  <label>مواصفات خليجية (GCC) / GCC</label>
                  <Select fullWidth hiddenLabel value={gcc} onChange={(e) => setGcc(e.target.value)} displayEmpty>
                    <MenuItem value="default" disabled>حدد / Select</MenuItem>
                    <MenuItem value="yes">نعم / Yes</MenuItem>
                    <MenuItem value="no">لا / No</MenuItem>
                  </Select>
                </div>

                <div className="control_group w_33">
                  <label>أميال / Miles</label>
                  <RadioGroup
                    row
                    value={miles}
                    onChange={(e) => setMiles(e.target.value)}
                    className="flex"
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="نعم / Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="لا / No" />
                  </RadioGroup>
                </div>
              </>
            )}

            {/* <div className="control_group w_33">
              <label>Manufacture Color</label>
              <TextField placeholder="Enter Color" hiddenLabel fullWidth value={manufactureColor} onChange={(e) => setManufactureColor(e.target.value)} />
            </div> */}

            <div className="control_group w_33">
              <label>طريقة الشراء / Purchase Method</label>
              <Select
                fullWidth
                hiddenLabel
                value={paymentMode}
                onChange={(e) => {
                  setPaymentMode(e.target.value);
                  if (e.target.value) setErrors(prev => ({ ...prev, paymentMode: "" }));
                }}
                displayEmpty
                error={!!errors.paymentMode}
              >
                <MenuItem value="" disabled>حدد طريقة الشراء / Select Purchase Method</MenuItem>
                <MenuItem value={PAYMENT_MODE.CASH}>شراء / Purchase</MenuItem>
                <MenuItem value={PAYMENT_MODE.ONLINE}>تأجير / Leasing</MenuItem>
                {/* <MenuItem value={PAYMENT_MODE.CARD}>Card payments</MenuItem> */}
              </Select>
              {errors.paymentMode && <FormHelperText error>{errors.paymentMode}</FormHelperText>}
            </div>
          </div>
        </div>

        <div className="cards">
          <div className="table_header mb_20">
            <h2>لون المركبة / Vehicle Color</h2>
          </div>

          {[VEHICLE_TYPE.BIKE, VEHICLE_TYPE.BICYCLE].includes(vehicleType) ? (
            <div className="cards bordered">
              <div className="gap_p">
                <div className="control_group w_100">
                  <label style={{ marginBottom: "15px" }}>اللون / Color <span className="c_danger">*</span></label>
                  <RadioGroup
                    value={selectedExteriorColor}
                    onChange={(e) => setSelectedExteriorColor(e.target.value)}
                    className="gap_p gap_sm"
                  >
                    {exteriorColors.map((color: any) => (
                      <div className="w_25" key={color._id}>
                        <FormControlLabel
                          value={color._id}
                          control={<Radio />}
                          label={
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "50%",
                                  backgroundColor: color.colorCode || (typeof color.name === 'object' ? color.name?.en || color.name?.name || "" : color.name),
                                  border: "1px solid #ccc",
                                }}
                              />
                              <span>{typeof color.name === 'object' ? color.name?.en || color.name?.name || "" : color.name}</span>
                            </div>
                          }
                        />
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="cards bordered">
                <div className="table_header mb_20">
                  <h3>الداخلي / Interior</h3>
                </div>

                <div className="gap_p">
                  <div className="control_group w_100">
                    <label style={{ marginBottom: "15px" }}>اللون / Color <span className="c_danger">*</span></label>

                    <RadioGroup
                      value={selectedInteriorColor}
                      onChange={(e) => setSelectedInteriorColor(e.target.value)}
                      className="gap_p gap_sm"
                    >
                      {interiorColors.map((color: any) => (
                        <div className="w_25" key={color._id}>
                          <FormControlLabel
                            value={color._id}
                            control={<Radio />}
                            label={
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "50%",
                                    backgroundColor: color.colorCode || (typeof color.name === 'object' ? color.name?.en || color.name?.name || "" : color.name),
                                    border: "1px solid #ccc",
                                  }}
                                />
                                <span>{typeof color.name === 'object' ? color.name?.en || color.name?.name || "" : color.name}</span>
                              </div>
                            }
                          />
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
              </div>

              <div className="cards bordered">
                <div className="table_header mb_20">
                  <h3>الخارجي / Exterior</h3>
                </div>

                <div className="gap_p">
                  <div className="control_group w_100">
                    <label style={{ marginBottom: "15px" }}>اللون / Color <span className="c_danger">*</span></label>

                    <RadioGroup
                      value={selectedExteriorColor}
                      onChange={(e) => setSelectedExteriorColor(e.target.value)}
                      className="gap_p gap_sm"
                    >
                      {exteriorColors.map((color: any) => (
                        <div className="w_25" key={color._id}>
                          <FormControlLabel
                            value={color._id}
                            control={<Radio />}
                            label={
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "50%",
                                    backgroundColor: color.colorCode || (typeof color.name === 'object' ? color.name?.en || color.name?.name || "" : color.name),
                                    border: "1px solid #ccc",
                                  }}
                                />
                                <span>{typeof color.name === 'object' ? color.name?.en || color.name?.name || "" : color.name}</span>
                              </div>
                            }
                          />
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="cards">
          <div className="table_header mb_20">
            <h2>الميزات / Features</h2>
          </div>

          {(() => {
            const featuresByGroup = featureGroups.reduce((acc: any, group: any) => {
              const gType = group.groupType || 0;
              if (!acc[gType]) acc[gType] = [];
              acc[gType].push(group);
              return acc;
            }, {});

            const getGroupTypeName = (value: number) => {
              if (!value) return "ميزات أخرى / Other Features";
              const entry = Object.entries(Group_Type).find(([_, val]) => val === value);
              if (!entry) return "ميزات أخرى / Other Features";
              return entry[0].split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
            };

            return Object.entries(featuresByGroup).map(([gTypeStr, groups]: [string, any]) => {
              const gType = Number(gTypeStr);
              return (
                <div key={gType} className="feature-group-section mb_20">
                  <h3 style={{ marginBottom: "15px", paddingBottom: "10px", borderBottom: "1px solid #1a1a1a1a", color: "#333", fontSize: "18px" }}>
                    {getGroupTypeName(gType)}
                  </h3>
                  {groups.map((group: any) => (
                    <div key={group._id || (typeof group.name === 'object' ? group.name?.en || group.name?.name : group.name)} className="cards bordered">
                      <div className="gap_p">
                        <div className="control_group w_100">
                          <label style={{ marginBottom: "10px", fontWeight: '600' }}>
                            {typeof group.name === 'object' ? group.name?.en || group.name?.name || "" : group.name}
                          </label>

                          {/* FIELD RENDERING LOGIC */}
                          {(() => {
                            const listItems = group.features?.length ? group.features : (group.options?.length ? group.options : []);
                            const groupId = group._id || (typeof group.name === 'object' ? group.name?.en || group.name?.name : group.name);
                            const currentValue = featureValues[groupId] || "";

                            const getID = (item: any) => {
                              if (!item) return "";
                              if (typeof item === 'string') return item;
                              return item._id || item.id || "";
                            };

                            const getLabel = (item: any) => {
                              if (typeof item === 'object') {
                                return item.name?.en || item.name?.name || item.name || "";
                              }
                              return item;
                            };

                            const getValue = (item: any) => {
                              return getID(item);
                            };

                            switch (group.fieldType) {
                              case Field_Type.SELECT:
                                return (
                                  <Select
                                    fullWidth
                                    hiddenLabel
                                    value={currentValue}
                                    onChange={(e) => handleFeatureChange(groupId, e.target.value)}
                                    displayEmpty
                                  >
                                    <MenuItem value="" disabled>حدد / Select {typeof group.name === 'object' ? group.name?.en || group.name?.name || "" : group.name}</MenuItem>
                                    {listItems.length > 0 ? (
                                      listItems.map((opt: any) => (
                                        <MenuItem key={getValue(opt)} value={getValue(opt)}>{getLabel(opt)}</MenuItem>
                                      ))
                                    ) : (
                                      <MenuItem value="" disabled>لا توجد خيارات متاحة / No options available</MenuItem>
                                    )}
                                  </Select>
                                );

                              case Field_Type.INPUT:
                                return (
                                  <TextField
                                    fullWidth
                                    hiddenLabel
                                    placeholder={`أدخل / Enter ${typeof group.name === 'object' ? group.name?.en || group.name?.name || "" : group.name}`}
                                    value={currentValue}
                                    onChange={(e) => handleFeatureChange(groupId, e.target.value)}
                                  />
                                );

                              case Field_Type.BOOLEAN:
                                return (
                                  <RadioGroup
                                    className="flex"
                                    row
                                    value={currentValue}
                                    onChange={(e) => handleFeatureChange(groupId, e.target.value)}
                                  >
                                    <FormControlLabel value="yes" control={<Radio />} label="نعم / Yes" />
                                    <FormControlLabel value="no" control={<Radio />} label="لا / No" />
                                  </RadioGroup>
                                );

                              case Field_Type.RADIO:
                                if (listItems.length > 0) {
                                  return (
                                    <RadioGroup
                                      className="gap_p gap_sm"
                                      value={currentValue}
                                      onChange={(e) => handleFeatureChange(groupId, e.target.value)}
                                    >
                                      {listItems.map((f: any) => (
                                        <div className="w_33" key={getValue(f)}>
                                          <FormControlLabel value={getValue(f)} control={<Radio />} label={getLabel(f)} />
                                        </div>
                                      ))}
                                    </RadioGroup>
                                  );
                                }
                                // Fallback for empty Radio: Treat as Yes/No
                                return (
                                  <RadioGroup
                                    className="flex"
                                    row
                                    value={currentValue}
                                    onChange={(e) => handleFeatureChange(groupId, e.target.value)}
                                  >
                                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                                    <FormControlLabel value="no" control={<Radio />} label="No" />
                                  </RadioGroup>
                                );

                              case Field_Type.CHECKBOX:
                              default: // Default to Checkbox
                                if (listItems.length > 0) {
                                  // For checkboxes, value should be an array
                                  const currentChecked = Array.isArray(currentValue) ? currentValue : [];

                                  const handleCheckboxChange = (featureValue: string, checked: boolean) => {
                                    let newChecked;
                                    if (checked) {
                                      newChecked = [...currentChecked, featureValue];
                                    } else {
                                      newChecked = currentChecked.filter((v: string) => v !== featureValue);
                                    }
                                    handleFeatureChange(groupId, newChecked);
                                  };

                                  return (
                                    <div className="gap_p gap_sm">
                                      {listItems.map((f: any) => (
                                        <div className="w_33" key={getValue(f)}>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={currentChecked.includes(getValue(f))}
                                                onChange={(e) => handleCheckboxChange(getValue(f), e.target.checked)}
                                              />
                                            }
                                            label={getLabel(f)}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  );
                                }
                                // Fallback: If no sub-features, render the Group Name as a single checkbox
                                return (
                                  <div className="gap_p gap_sm">
                                    <div className="w_100">
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={!!currentValue}
                                            onChange={(e) => handleFeatureChange(groupId, e.target.checked)}
                                          />
                                        }
                                        label={typeof group.name === 'object' ? group.name?.en || group.name?.name || "" : group.name}
                                      />
                                    </div>
                                  </div>
                                );
                            }
                          })()}

                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            });
          })()}
        </div>

        {/* SAVE BUTTON */}
        <div className="form_btn">
          <Button className="btn btn_primary" onClick={handleSubmit} disabled={isAdding || isUploading}>
            {isAdding ? <CircularProgress size={20} color="inherit" /> : "حفظ المركبة / Save Vehicle"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AddEditVehicle;